import { useAuthContext } from './context';
import type { App } from '@bapecom/types';

export const useAuth = () => {
  const { user, isAuthenticated, permissions } = useAuthContext();
  return { user, isAuthenticated, permissions };
};

export const useUser = () => {
  const { user } = useAuthContext();
  return user;
};

export const usePermissions = () => {
  const { permissions } = useAuthContext();
  return permissions;
};

export const useCanAccessApp = (appId: string) => {
  const { user, permissions } = useAuthContext();
  
  // Admin can access everything
  if (user?.role === 'admin') return true;
  
  // Check specific permission
  const permission = permissions.find((p) => p.appId === appId);
  return permission?.canView ?? false;
};

export const useCanEditApp = (appId: string) => {
  const { user, permissions } = useAuthContext();
  
  // Admin can edit everything
  if (user?.role === 'admin') return true;
  
  // Check specific permission
  const permission = permissions.find((p) => p.appId === appId);
  return permission?.canEdit ?? false;
};

export const useAccessibleApps = (allApps: App[]) => {
  const { user, permissions } = useAuthContext();
  
  if (!user) return [];
  
  // Admin sees all apps
  if (user.role === 'admin') return allApps;
  
  // Get app IDs from user.apps (array of strings) or permissions
  const userApps: string[] = (user as any).apps || [];
  
  // If user has apps defined, filter by those
  if (userApps.length > 0) {
    return allApps.filter((app) => userApps.includes(app.id));
  }
  
  // Fallback: check permissions array (legacy format)
  if (permissions.length > 0) {
    return allApps.filter((app) => {
      const permission = permissions.find((p) => p.appId === app.id);
      return permission?.canView ?? false;
    });
  }
  
  // No apps configured - show nothing
  return [];
};
